<template>
  <div>
    <b-button 
      :disabled="fetching"
      :title="linkText" 
      variant="outline-secondary" 
      @click="showMediaPicker">
      <i class="fa fa-sm fa-photo-video" ></i>
    </b-button>

    <b-modal
      v-model="showModal"
      title="Media Library"
      centered hide-footer
      size="lg">
      <div v-if="current" class="mediaPickerTable">
        <table class="table">
          <tr>
            <th colspan="3">Current Media</th>
          </tr>
          <tr>
            <td>
              <div class="name-wrapper">
                <span v-if="current.type === 'pdf'" class="pdf">
                  <i class="fas fa-file-pdf fa-3x"></i>
                </span>

                <span v-if="current.type !== 'pdf'" class="thumbnail">
                  <img :src="getThumbnail(current)">
                </span>
                <span class="name">
                  {{ current.name }}
                </span>  
              </div>
            </td>
            <td>{{ mediaType(current) }}</td>
            <td class="nowrap text-right">
              <span v-b-tooltip.hover style="display:inline-block" title="remove">
                <b-button
                  :disabled="fetching"
                  class="table-button"
                  title="remove"
                  variant="outline-secondary"
                  @click.stop="remove">
                  <i class="fas fa-trash"></i>
                </b-button>
              </span>
            </td>
          </tr>
        </table>
      </div>

      <b-container fluid>
        <b-row class="table-header">

          <b-col cols="12" sm="6" lg="3" class="ml-auto">
            <b-form-group>
              <b-form-select
                :options="moduleNames"
                :disabled="fetching"
                v-model="params.module"
                @change="filterByModule"/>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="6" lg="3">
            <b-form-group>
              <b-form-select
                :options="mediaTypes"
                :disabled="fetching"
                v-model="params.type"
                @change="filterByType"/>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="6" lg="3">
            <b-form-group>
              <b-form-input
                ref="search"
                :disabled="fetching"
                v-model="search"
                placeholder="Search by name"
                @keyup.native="doSearch"
                @input="searchChanged"/>
            </b-form-group>
          </b-col>

        </b-row>
      </b-container>

      <no-data v-if="noData" message="No media found..."/>
      
      <b-table
        v-if="media.length"
        :hover="!fetching"
        :responsive="true"
        :busy="fetching"
        :items="media"
        :fields="tableFields"
        :per-page="Number(meta.per_page)"
        :sort-by="meta.order"
        :sort-desc="meta.desc"
        :no-local-sorting="true"
        @sort-changed="sortChanged"
        @row-clicked="rowClicked">

        <template slot="name" slot-scope="data">
          <div class="name-wrapper">
            <!-- v-if is buggy here -->
            <span :class="{ 'hidden' : data.item.type !== 'pdf' }" class="pdf">
              <i class="fas fa-file-pdf fa-3x"></i>
            </span>
            <span :class="{ 'hidden' : data.item.type === 'pdf' } " class="thumbnail">
              <img v-if="data.item.thumbnail" :src="data.item.thumbnail.url">
              <img v-else :src="data.item.url">
            </span>
            <span class="name">
              {{ data.value }}
            </span>
          </div>
        </template>

        <template slot="created_at" slot-scope="data">
          {{ readableDate(data.value) }} 
        </template>

        <template slot="actions" slot-scope="data">
          <div class="no-wrap">           
            <span v-b-tooltip.hover style="display:inline-block" title="view">
              <b-button 
                :disabled="fetching"
                class="table-button"
                title="view"
                variant="outline-secondary"
                @click.stop="viewMedia(data.item)">
                <i class="fas fa-eye"></i>
              </b-button>
            </span>
          </div>
        </template>

      </b-table>

      <b-row>
        <b-col cols="12">
          <b-pagination
            v-if="media.length"
            :disabled="fetching"
            :value="Number(meta.current_page)"
            :total-rows="Number(meta.total)"
            :per-page="Number(meta.per_page)"
            @change="pageChanged"/>
        </b-col>
      </b-row>

    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import mediaApi from '@/api/media'
import moduleApi from '@/api/modules'
import {mapState} from 'vuex'
import imagePreview from '@/components/common/image-preview'
import noData from '@/components/common/table/no-data'
import { mapMimeTypeToGeneralType, readableDate } from '@/helpers'

export default {
  components:{
    imagePreview,
    noData
  },
  props:{
    linkText: { type: String, default: '+ add media' },
    tenantId: { type: String, required: true },
    moduleId: { type: String, required: true },
    current: { type: Object, required: false, default: null }
  },
  data(){
    return {
      showModal: false,
      media: [],
      meta: {}, 
      fetching: false,
      mediaTypes: [
        { text: 'All Types', value: null },
        { text: 'images', value: 'image' },
        { text: 'videos', value: 'video' },
        { text: 'pdfs', value: 'pdf' },
        { text: 'audio files', value: 'audio' }
      ],
      moduleNames: [],
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'type', label: 'Type', sortable: false },
        { key: 'created_at', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      params: {},
      search: ''
    }
  },
  computed: {
    ...mapState('tenants', {
      organization: 'current'
    }), 
    noData(){
      return this.fetching? false: this.media.length < 1
    }
  },
  methods: {
    readableDate,
    close(){
      this.showModal = false
    },
    getThumbnail(media){
      return media.thumbnail? media.thumbnail.url: (media.url || 'none')
    },
    select(media){
      this.$emit('selected', media)
      this.close()
    },
    remove(){
      this.$emit('remove')
      this.close()
    },
    async showMediaPicker(){
      this.params = {
        current_page: 1,
        module : this.moduleId,
        search: null,
        tenantID : this.tenantId,
        type: null
      }
      
      await this.loadModuleNames()
      await this.fetch()
    },
    async loadModuleNames(){
      this.fetching = true
      const params = {
        'order': 'name',
        'tenantID': this.organization.id
      }
      const response = await moduleApi.index(params)
      let names = response.data.data.map((module) => {
        return {
          text: module.name,
          value: module.id
        }
      })
      
      names = _.sortBy(_.filter(names, (m) => {return m.deleted_at == null}), 'text')
      names.unshift({text: 'All Modules', value: null})

      this.moduleNames =  names
    },
    async fetch(){
      this.fetching = true
      this.showModal = true

      const response = await mediaApi.index(this.params)
      this.meta = response.data.meta

      this.media = _.map(_.filter(response.data.data, (m) => {return this.current == null || m.id != this.current.id}), (m) => { m.type = mapMimeTypeToGeneralType(m.mime_type); return m})
      this.fetching = false
    },
    mediaType(media){
      return mapMimeTypeToGeneralType(media.mime_type)
    },
    viewMedia (media) {
      window.open(media.url)
    },
    rowClicked(item, index, e){
      this.select(item)
    },
    filterByType(type){
      this.params.type = type
      this.params.page = 1
      this.fetch()
    },
    filterByModule(module){
      this.params.module = module
      this.params.page = 1
      this.fetch()
    },
    doSearch(event){
      if(event.key === 'Enter'){
        this.params.search = this.meta.search
        this.fetch()
      }
    },
    searchChanged: _.debounce(function(value){
      this.params.page = 1
      this.params.search = value
      if(!this.fetching){
        this.fetch()
      }
    },750),
    sortChanged(event){
      this.params.page = 1
      this.params.order = event.sortBy
      this.params.desc = event.sortDesc
      this.fetch()
    },
    pageChanged(page){
      this.params.page = page
      this.fetch()
    }
  }
}
</script>

<style lang="scss" scoped>
.mediaPickerTable{
  th
  {
    border-top:none;
  }
}
.name-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .pdf {
    width: 8rem;
    height: 6rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .thumbnail {
    width: 8rem;
    height: 6rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: auto;
      max-width: 8rem;
      height: auto;
      max-height: 6rem;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/pages/admin/knowledge-checks/media/mediaPicker.vue